import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useState } from "react";
import { useLocale, useTranslations } from "use-intl";

import { Card, CardContent, CardHeader, CardTitle } from "@lawline/ui/components/card";
import { Skeleton } from "@lawline/ui/components/skeleton";

import { ExternalLink } from "@platform/components/ui/external-link";
import { docsLink } from "@platform/core/docs";
import { UsageAreaChart } from "./components/usage-area-chart";
import { UsageBreakdownTable } from "./components/usage-breakdown-table";
import { UsageRangeTabs } from "./components/usage-range-tabs";
import { UsageSummaryCards } from "./components/usage-summary-cards";
import { TOKEN_SERIES } from "./usage.constants";
import { usageSeriesQueryOptions, usageSummaryQueryOptions } from "./usage.queries";
import type { UsageRange } from "./usage.types";
import { isKnownSource, titleCaseSource } from "./usage.utils";

export function UsagePage() {
	const t = useTranslations("usage");
	const locale = useLocale();
	const tChart = useTranslations("usage.chart");
	const tRequests = useTranslations("usage.requestsChart");
	const tBreakdown = useTranslations("usage.breakdown");
	const tSources = useTranslations("usage.sources");

	const [range, setRange] = useState<UsageRange>("billing");
	const series = useQuery(usageSeriesQueryOptions(range, "day"));
	const summary = useQuery(usageSummaryQueryOptions(range));

	// Both charts share the series query, so its loading/error/empty states render
	// identically; only the title and chart component differ.
	const seriesCard = (
		title: string,
		chart: (data: NonNullable<typeof series.data>) => ReactNode,
	) => (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">{title}</CardTitle>
			</CardHeader>
			<CardContent>
				{series.isPending ? (
					<Skeleton className="h-[320px] w-full" />
				) : series.error ? (
					<p className="text-destructive flex h-[320px] items-center justify-center text-sm">
						{tChart("error")}
					</p>
				) : series.data.points.length === 0 ? (
					<p className="text-muted-foreground flex h-[320px] items-center justify-center text-sm">
						{tChart("empty")}
					</p>
				) : (
					chart(series.data)
				)}
			</CardContent>
		</Card>
	);

	return (
		<section className="mx-auto mt-2 flex w-full max-w-5xl flex-col gap-6">
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-semibold">{t("title")}</h1>
				<UsageRangeTabs value={range} onChange={setRange} />
			</div>

			<p className="text-muted-foreground text-sm">
				{t.rich("docsIntro", {
					limits: (chunks) => (
						<ExternalLink href={docsLink("limits", locale)}>{chunks}</ExternalLink>
					),
				})}
			</p>

			{summary.isPending ? (
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
					<Skeleton className="h-24 w-full" />
					<Skeleton className="h-24 w-full" />
					<Skeleton className="h-24 w-full" />
				</div>
			) : summary.error ? null : (
				<UsageSummaryCards totals={summary.data.totals} />
			)}

			{seriesCard(tChart("title"), (data) => (
				<UsageAreaChart
					points={data.points}
					bucket={data.bucket}
					stacked
					series={TOKEN_SERIES.map((s) => ({
						...s,
						label: tChart(`series.${s.key}`),
					}))}
				/>
			))}

			{summary.error ? (
				<p className="text-destructive text-sm">{tBreakdown("error")}</p>
			) : summary.isPending ? (
				<Skeleton className="h-40 w-full" />
			) : (
				<>
					<Card>
						<CardContent>
							<UsageBreakdownTable
								head={tBreakdown("byApiKey")}
								empty={tBreakdown("empty")}
								rows={summary.data.byKey.map((k) => ({
									id: k.apiKeyId,
									label: k.name ?? t("unnamedApiKey"),
									requests: k.requests,
									tokens: k.tokens,
									lawline: k.lawline,
								}))}
							/>
						</CardContent>
					</Card>

					<Card>
						<CardContent>
							<UsageBreakdownTable
								head={tBreakdown("bySource")}
								empty={tBreakdown("empty")}
								rows={summary.data.bySource.map((s) => ({
									id: s.source,
									label: isKnownSource(s.source) ? tSources(s.source) : titleCaseSource(s.source),
									requests: s.requests,
									tokens: s.tokens,
									lawline: s.lawline,
								}))}
							/>
						</CardContent>
					</Card>
				</>
			)}

			{seriesCard(tRequests("title"), (data) => (
				<UsageAreaChart
					points={data.points}
					bucket={data.bucket}
					series={[
						{ key: "requests", color: "#3b82f6", label: tRequests("series") }, // blue-500
					]}
				/>
			))}
		</section>
	);
}
